import initialize, { interceptAxios, handleLogout } from '@patsnap-utils/passport';
import type psHttp from '@patsnap/common-ps-http';
import { psAxios, setPostByFormPassport } from '@patsnap/common-ps-http';
import type { DeployEnv } from '@patsnap-utils/biz';
import { UserCenterService, transformUrlProxy, SLD, Site } from '@patsnap-utils/biz';
import type { AxiosRequestConfig, AxiosError } from 'axios';
import _ from 'lodash';
import type { ISyncTokenParams } from '@patsnap-utils/passport/src/type';
import { applySignInterceptors } from './sign';
import { sleep } from '@eureka/utils/task';
import qs from 'qs';
import { removeEurekaLoginCookie } from './set-eureka-login-cookie';
import type Passport from '@patsnap-utils/passport/src/passport';
import { create } from '@eureka/utils/deferred';

export const EUREKA_API_VERSION = '1.0';

type Rule =
  | RegExp
  | {
      pattern: RegExp;
      version: string;
    };

export interface IProjectPreset {
  config?: AxiosRequestConfig;
  name?: string;
  apiVersion?: string;
  excludesUrlRegExps?: Rule[];
  callback?: (axios: typeof psHttp) => unknown;
  preRedirectLogin?: (error: unknown) => unknown;
  region?: DeployEnv;
}

const { set, get, isRegExp } = _;

const PASSPORT_URI = process.env.VUE_APP_PASSPORT_URI;
const CLIENT_ID = process.env.VUE_APP_PASSPORT_CLIENT_ID;
const CLIENT_NAME = process.env.VUE_APP_PASSPORT_CLIENT_NAME;

const baseAuthServer = transformUrlProxy(SLD.PASSPORT, PASSPORT_URI as string);

let _header = {};

export const _passportDeferred = create<Passport>();

export async function getAsyncToken() {
  const _passport = await _passportDeferred.promise;
  return _passport?.getAccessToken().then(
    (token) => {
      if (!token) {
        throw new Error('token is empty');
      }
      return token;
    },
    () => {
      return '';
    },
  );
}

export const passportInterceptManager = {
  inject() {
    if (!psAxios.psInterceptors.passportRequestInterceptor) {
      interceptAxios(psAxios);
    }
  },
  eject() {
    psAxios.interceptors.request.eject(psAxios.psInterceptors.passportRequestInterceptor);
    psAxios.interceptors.response.eject(psAxios.psInterceptors.passportResponseInterceptor);
    delete psAxios.psInterceptors.passportRequestInterceptor;
    delete psAxios.psInterceptors.passportResponseInterceptor;
  },
};

function replaceLocation(url: string) {
  console.log('location replace:', url);
  window.location.replace(url);
}

export function stoper() {
  if (window.location.search.indexOf('noraven') > -1) {
    throw new Error('Local stop redirect');
  }
}

const handleExcludesUrlRegExps = (option: IProjectPreset) => {
  for (const rule of option.excludesUrlRegExps || []) {
    let pattern: RegExp;
    let version = option.apiVersion;
    if (isRegExp(rule)) {
      pattern = rule;
    } else {
      pattern = rule.pattern;
      version = rule.version;
    }
    if (option.config?.url && pattern.test(option.config.url)) {
      /**
       * 如果 API （封装）未指定 header: X-API-Version，才使用 preset 中配置的 version
       */
      if (!('X-API-Version' in option.config!.headers!) && version) {
        option.config!.headers!['X-API-Version'] = version;
      }
      return option.config;
    }
  }
};

function revertString(str: string) {
  return str.split('').reverse().join('');
}

async function prepareTokenBase(option: IProjectPreset) {
  const initializeConfig = {
    baseAuthServer,
    clientId: CLIENT_ID,
    clientName: CLIENT_NAME,
    onTokenRefresh: () => {
      //
    },
    redirectToLogin(error: AxiosError) {
      // 跳转登录前先删除 EurekaLogin Cookie, preRedirectLogin 回抛错误,所以需要写到最前面
      removeEurekaLoginCookie();
      option.preRedirectLogin && option.preRedirectLogin(error);
      console.warn('Check Session Error, Will Redirect To Login Page!');
      stoper();
      const errcode = get(error, 'errcode');
      if (errcode) {
        replaceLocation(
          UserCenterService.createLogoutHref(true, {
            errorCode: errcode,
            banner_type: 'image',
            anonymousLogin: false,
          }),
        );
      } else {
        replaceLocation(
          UserCenterService.createLogoutHref(true, {
            errorCode: 39999,
            banner_type: 'image',
            anonymousLogin: false,
          }),
        );
      }
      return sleep(6000); // 正在跳转（大概6秒够了），后面的别执行了
    },
    /*  可选，允许修改 checksession 的参数 */
    syncTokenParamsModifier(params: ISyncTokenParams) {
      return { ...params /* , flag: 'analytics', */ };
    },
    detectResponseAsUnauthorized(error: AxiosError) {
      removeEurekaLoginCookie();
      const redirectUrl = get(error, 'response.data.redirectUrl');
      if (redirectUrl) {
        stoper();
        window.location.href = redirectUrl;
        throw error;
      }
      return get(error, 'response.status') === 401;
    },
    onPaymentRequired(error: any) {
      // 自己跳转到付费页面
      const errcode = get(error, 'errcode');
      const urlParams = {
        from: CLIENT_NAME,
        client_id: CLIENT_ID,
        redirect_uri: encodeURIComponent(window.location.href),
        errorCode: errcode,
      };
      const url = new Site({
        sld: SLD.ACCOUNT,
        pathname: `user-settings/#/upper-limit${qs.stringify(urlParams, { addQueryPrefix: true })}`,
      }).href;
      stoper();
      window.location.replace(url);
    },
    onMfaRequired(err: any) {
      const urlParams = {
        from: CLIENT_NAME,
        client_id: CLIENT_ID,
        redirect_uri: encodeURIComponent(window.location.href),
        errorCode: get(err, 'errcode'),
      };
      const url = new Site({
        sld: SLD.ACCOUNT,
        pathname: `user-settings/#/mfa-required${qs.stringify(urlParams, { addQueryPrefix: true })}`,
      }).href;
      window.location.replace(url);
    },
  };

  const interceptorRequestSuccess = (config: AxiosRequestConfig) => {
    if (config.headers) {
      config.headers['X-PatSnap-From'] = `w-${CLIENT_NAME}`;
    }

    config.headers = {
      ...config.headers,
      ..._header,
    };

    const excludesUrlConfig = handleExcludesUrlRegExps({
      config,
      excludesUrlRegExps: option.excludesUrlRegExps,
      apiVersion: option.apiVersion,
    });

    if (excludesUrlConfig) return excludesUrlConfig;

    return config;
  };

  // @ts-ignore
  psAxios.psInterceptors.nodeApiInterceptor = psAxios.interceptors.request.use(
    interceptorRequestSuccess,
    undefined,
  );

  /* 清除 csrfTokenInterceptor */
  psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);

  passportInterceptManager.inject();

  window.customBeforeLogout = function customBeforeLogout() {
    // 此方法默认会被 Header V4点"登出"时调用， 用其他头时，登出时自己调用 handleLogout()
    return handleLogout();
  };

  option.callback && option.callback(psAxios);
  const passport = await initialize(initializeConfig as any);
  set(window, revertString('6csg9md32h'), function () {
    return passport.getToken('token') || '';
  });
  applySignInterceptors(psAxios);

  setPostByFormPassport(passport);

  _passportDeferred.resolve(passport);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function addionJwtHeaderInterceptor(config: AxiosRequestConfig) {
    try {
      const token = await getAsyncToken();
      const strings = token.split('.');
      const jwt = JSON.parse(
        decodeURIComponent(escape(window.atob(strings[1].replace(/-/g, '+').replace(/_/g, '/')))),
      );
      const { sub = '', authorities = [], tenant_id = '', client_id = '' } = jwt;
      const addionHeaders: Record<string, any> = {};
      addionHeaders['X-User-ID'] = sub;
      addionHeaders['X-User-Roles'] = authorities;
      addionHeaders['X-Tenant-ID'] = tenant_id;
      addionHeaders['X-Client-ID'] = client_id;
      config.headers = _.merge({}, config.headers, addionHeaders);
    } catch (err) {
      console.error('Parse jwt error');
    }
    return config;
  }

  return passport;
}

/**
 * 更新 token base 请求的 header， 如 X-Site-Lang
 */
function updateTokenBaseRequestHeaders(headers: Record<string, unknown>): void {
  _header = headers;
}

export { baseAuthServer, prepareTokenBase, updateTokenBaseRequestHeaders };
